// src/features/datev-weiterberechnung/services/datevWeiterberechnungService.ts

/**
 * Service für das Matching zwischen geparsten DATEV-Mandanten und XTAX-Kunden
 * sowie die Ausführung der Weiterberechnung (Positionen in Rechnungsentwürfe einfügen
 * bzw. neue Entwürfe erstellen).
 *
 * Architektur-Hinweis:
 * Anders als invoiceService.createDraft werden hier KEINE Sammelkunden-Rechnungen
 * erzeugt – Kunden mit gesetztem auftraggeber_id werden im Matching bereits übersprungen.
 *
 * Die Analyse-Auswertungen (nicht weiterberechnete Posten, Artikel- und Kunden-Rangliste)
 * sind reine Lesefunktionen. Die Ausführung verarbeitet unverändert ausschliesslich
 * Matches mit status='ok'.
 *
 * DATEV-Mandantennummern ohne eigenen Kundendatensatz (z.B. nach einer Verschmelzung)
 * werden über die Tabelle datev_mandanten_zuordnung einem Kunden zugeordnet. Dieselbe
 * Auflösung nutzt die Kostenerfassung für die Unit Economics.
 */

import { supabase } from '../../../shared/services/supabase';
import { invoiceService, buildRechnungsEmpfaengerPerson } from '../../../services/invoice/invoiceService';
import {
  ParsedDatevInvoice,
  ParsedMandant,
  AggregatedPosition,
  NonBillablePosition,
  MandantMatch,
  MatchStatus,
  TargetInvoiceInfo,
  TargetAction,
  ArtikelRankingEntry,
  KundenRankingEntry,
  WeiterberechnungAnalysis,
  ExecutionResult,
  ExecuteOptions,
  WEITERBERECHNUNG_WHITELIST,
  isWhitelistArtikel
} from '../types';

// Maximale Anzahl Monate, die wir nach vorne suchen, wenn der aktuelle Monat bereits
// freigegeben/versendet ist. Schutz vor Endlosschleife.
const MAX_LOOKAHEAD_MONTHS = 12;

// Toleranz beim Abgleich der geparsten Blocksumme gegen die DATEV-Kopfsumme
const SUMMEN_TOLERANZ = 0.01;

// Toleranz beim Abgleich der Summe aller Blockkopfsummen gegen den Nettobetrag
// laut PDF-Fußbereich. Diese Gegenprobe deckt Lücken auf, die innerhalb eines
// Blocks nicht auffallen, etwa einen komplett nicht erkannten Mandantenblock.
const GESAMTSUMMEN_TOLERANZ = 0.01;

// ============================================
// HELPER: Monat/Jahr weiterschieben
// ============================================

function addMonths(year: number, month: number, offset: number): { year: number; month: number } {
  const zeroBased = (month - 1) + offset;
  const newYear = year + Math.floor(zeroBased / 12);
  const newMonth = (zeroBased % 12 + 12) % 12 + 1;
  return { year: newYear, month: newMonth };
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

// ============================================
// HELPER: Positionen aggregieren
// ============================================

/**
 * Filtert Positionen auf die Whitelist und aggregiert pro Artikelnummer.
 * Mehrfaches Vorkommen desselben Artikels im selben Mandantenblock (mit unterschiedlichen
 * Leistungsdaten) wird zu einer Zeile zusammengefasst: Menge summiert, Gesamtpreis summiert,
 * Einzelpreis neu berechnet.
 */
function aggregateWhitelistPositions(mandant: ParsedMandant): AggregatedPosition[] {
  const map = new Map<string, AggregatedPosition>();

  for (const pos of mandant.positionen) {
    if (!isWhitelistArtikel(pos.artikelnr)) continue;

    const existing = map.get(pos.artikelnr);
    if (existing) {
      existing.menge += pos.menge;
      existing.gesamtpreis = Math.round((existing.gesamtpreis + pos.gesamtpreis) * 100) / 100;
      existing.einzelpreis = existing.menge !== 0
        ? Math.round((existing.gesamtpreis / existing.menge) * 10000) / 10000
        : 0;
    } else {
      const einzelpreis = pos.menge !== 0
        ? Math.round((pos.gesamtpreis / pos.menge) * 10000) / 10000
        : 0;
      map.set(pos.artikelnr, {
        artikelnr: pos.artikelnr,
        bezeichnung: WEITERBERECHNUNG_WHITELIST[pos.artikelnr],
        menge: pos.menge,
        gesamtpreis: Math.round(pos.gesamtpreis * 100) / 100,
        einzelpreis,
        einheit: 'Stk'
      });
    }
  }

  // Sortiert nach Artikelnummer zurückgeben (für konsistente Reihenfolge)
  return Array.from(map.values()).sort((a, b) => a.artikelnr.localeCompare(b.artikelnr));
}

/**
 * Aggregiert die Posten, die NICHT weiterberechnet werden.
 *
 * Aggregationsschlüssel ist Artikelnummer + USt-Satz, damit die Anzeige der
 * DATEV-Rechnung entspricht (derselbe Artikel kann in zwei USt-Sätzen erscheinen,
 * z.B. das DATEV Basis-Angebot mit 19 % und 7 %).
 *
 * @param alleEinbeziehen true = auch Whitelist-Artikel als nicht weiterberechenbar
 *        führen (nötig beim beraterbezogenen Block, der keinem Kunden zuzuordnen ist)
 */
function aggregateNonBillablePositions(
  mandant: ParsedMandant,
  alleEinbeziehen: boolean
): NonBillablePosition[] {
  const map = new Map<string, NonBillablePosition>();

  for (const pos of mandant.positionen) {
    if (!alleEinbeziehen && isWhitelistArtikel(pos.artikelnr)) continue;

    const key = `${pos.artikelnr || 'P:' + pos.bezeichnung}|${pos.ustSatz}`;
    const existing = map.get(key);
    if (existing) {
      existing.menge += pos.menge;
      existing.gesamtpreis = round2(existing.gesamtpreis + pos.gesamtpreis);
    } else {
      map.set(key, {
        artikelnr: pos.artikelnr,
        bezeichnung: pos.bezeichnung,
        menge: pos.menge,
        ustSatz: pos.ustSatz,
        gesamtpreis: round2(pos.gesamtpreis)
      });
    }
  }

  // Absteigend nach Betrag, damit die grössten Kostenblöcke oben stehen
  return Array.from(map.values()).sort((a, b) => b.gesamtpreis - a.gesamtpreis);
}

// ============================================
// HELPER: Analyse-Felder eines Blocks
// ============================================

interface AnalyseFelder {
  nonBillablePositions: NonBillablePosition[];
  totalNonBillable: number;
  datevSummeLautKopf: number;
  parsedSumme: number;
  summenAbweichung: number | null;
  ungeparsteZeilen: string[];
}

/**
 * Berechnet die Analyse-Felder eines Blocks: nicht weiterberechnete Posten,
 * geparste Summe und Abweichung gegen die DATEV-Kopfsumme.
 *
 * Die DATEV-Kopfsumme ist der maßgebliche Anzeigewert. Weicht die Summe der
 * geparsten Posten davon ab, wird das ausgewiesen statt kaschiert – so werden
 * Parser-Lücken sichtbar.
 */
function buildAnalyseFelder(mandant: ParsedMandant, alleEinbeziehen: boolean): AnalyseFelder {
  const nonBillablePositions = aggregateNonBillablePositions(mandant, alleEinbeziehen);
  const totalNonBillable = round2(
    nonBillablePositions.reduce((sum, p) => sum + p.gesamtpreis, 0)
  );

  const parsedSumme = round2(
    mandant.positionen.reduce((sum, p) => sum + p.gesamtpreis, 0)
  );
  const datevSummeLautKopf = round2(mandant.gesamtbetragLautDatev);
  const diff = round2(parsedSumme - datevSummeLautKopf);
  const summenAbweichung = Math.abs(diff) > SUMMEN_TOLERANZ ? diff : null;

  return {
    nonBillablePositions,
    totalNonBillable,
    datevSummeLautKopf,
    parsedSumme,
    summenAbweichung,
    ungeparsteZeilen: mandant.ungeparsteZeilen
  };
}

// ============================================
// HELPER: Zielrechnung ermitteln
// ============================================

/**
 * Ermittelt, in welche Rechnung die Weiterberechnungs-Positionen geschrieben werden sollen.
 *
 * Logik:
 * - Existiert ein Entwurf für year/month → append_draft
 * - Existiert eine freigegebene/versendete Rechnung für year/month → schaue Folgemonat (rekursiv)
 * - Existiert gar keine Rechnung für year/month → create_draft
 */
async function determineTargetInvoice(
  customerId: string,
  sourceYear: number,
  sourceMonth: number
): Promise<TargetInvoiceInfo> {
  let currentYear = sourceYear;
  let currentMonth = sourceMonth;
  let monthsForward = 0;

  while (monthsForward <= MAX_LOOKAHEAD_MONTHS) {
    const { data: existing } = await supabase
      .from('invoices')
      .select('id, status')
      .eq('customer_id', customerId)
      .eq('year', currentYear)
      .eq('month', currentMonth)
      .maybeSingle();

    if (!existing) {
      // Keine Rechnung für diesen Monat → neuen Entwurf anlegen
      const action: TargetAction = monthsForward === 0 ? 'create_draft' : 'create_draft_next_month';
      return {
        action,
        targetYear: currentYear,
        targetMonth: currentMonth,
        existingInvoiceId: null,
        existingInvoiceStatus: null
      };
    }

    if (existing.status === 'entwurf') {
      // Entwurf vorhanden → anhängen
      const action: TargetAction = monthsForward === 0 ? 'append_draft' : 'append_draft_next_month';
      return {
        action,
        targetYear: currentYear,
        targetMonth: currentMonth,
        existingInvoiceId: existing.id,
        existingInvoiceStatus: existing.status
      };
    }

    // Rechnung existiert, ist aber schon freigegeben/versendet/importiert → Folgemonat prüfen
    const next = addMonths(currentYear, currentMonth, 1);
    currentYear = next.year;
    currentMonth = next.month;
    monthsForward++;
  }

  // Fallback (sollte nie erreicht werden): create im letzten geprüften Monat
  return {
    action: 'create_draft_next_month',
    targetYear: currentYear,
    targetMonth: currentMonth,
    existingInvoiceId: null,
    existingInvoiceStatus: null
  };
}

// ============================================
// MATCHING
// ============================================

export interface CustomerLookup {
  id: string;
  company: string;
  mandantennummer: string | null;
  deleted: boolean | null;
  auftraggeber_id: string | null;
}

/**
 * Lädt alle Kunden mit DATEV-Mandantennummer (ohne 1000-Zeilen-Limit)
 */
async function loadCustomersByMandantennummer(): Promise<Map<string, CustomerLookup>> {
  const { data, error } = await supabase
    .from('customers')
    .select('id, company, mandantennummer, deleted, auftraggeber_id')
    .not('mandantennummer', 'is', null)
    .limit(5000);

  if (error) {
    console.error('[DatevWeiterberechnung] Fehler beim Laden der Kunden:', error);
    throw new Error('Kunden konnten nicht geladen werden: ' + error.message);
  }

  const map = new Map<string, CustomerLookup>();
  for (const c of (data || []) as CustomerLookup[]) {
    if (!c.mandantennummer) continue;

    const existing = map.get(c.mandantennummer);
    if (!existing) {
      map.set(c.mandantennummer, c);
      continue;
    }

    // Mehrere Kunden auf derselben DATEV-Mandantennummer: aktive Kunden haben Vorrang
    // vor gelöschten. So ist das Ergebnis unabhängig von der Reihenfolge, in der die
    // Datenbank die Zeilen liefert.
    const existingDeleted = existing.deleted === true;
    const currentDeleted = c.deleted === true;

    if (existingDeleted && !currentDeleted) {
      map.set(c.mandantennummer, c);
      continue;
    }

    if (!existingDeleted && !currentDeleted) {
      // Zwei aktive Kunden auf derselben Mandantennummer sind fachlich nicht
      // entscheidbar. Der zuerst gefundene bleibt; der Fall wird sichtbar gemacht.
      console.warn(
        `[DatevWeiterberechnung] Mandantennummer ${c.mandantennummer} ist mehrfach aktiv ` +
        `vergeben ("${existing.company}" und "${c.company}"). Verwendet wird "${existing.company}". ` +
        'Bitte Stammdaten prüfen.'
      );
    }
  }
  return map;
}

/**
 * Wendet die hinterlegten Mandantenzuordnungen auf die Kundenliste an.
 *
 * Eine Zuordnung in datev_mandanten_zuordnung ist eine ausdrückliche Entscheidung
 * und hat deshalb Vorrang vor dem Abgleich über die Mandantennummer. Ohne
 * Beraternummer im Rechnungskopf wird keine Zuordnung angewendet, weil sie je
 * Beraternummer hinterlegt ist.
 */
async function wendeMandantenZuordnungenAn(
  map: Map<string, CustomerLookup>,
  beraternummer: string | null
): Promise<void> {
  if (!beraternummer) return;

  const { data, error } = await supabase
    .from('datev_mandanten_zuordnung')
    .select('datev_mandantennummer, customer:customers!datev_mandanten_zuordnung_customer_id_fkey(id, company, mandantennummer, deleted, auftraggeber_id)')
    .eq('beraternummer', beraternummer);

  if (error) {
    console.error('[DatevWeiterberechnung] Fehler beim Laden der Mandantenzuordnungen:', error);
    throw new Error('Mandantenzuordnungen konnten nicht geladen werden: ' + error.message);
  }

  for (const row of (data || []) as unknown as Array<{
    datev_mandantennummer: string;
    customer: CustomerLookup | CustomerLookup[] | null;
  }>) {
    const kunde = Array.isArray(row.customer) ? row.customer[0] : row.customer;
    if (!kunde) {
      throw new Error(
        `Die Mandantenzuordnung für DATEV-Mandant ${row.datev_mandantennummer} verweist auf keinen Kunden.`
      );
    }

    const bisher = map.get(row.datev_mandantennummer);
    if (bisher && bisher.deleted !== true && bisher.id !== kunde.id) {
      console.warn(
        `[DatevWeiterberechnung] DATEV-Mandant ${row.datev_mandantennummer} ist dem aktiven Kunden ` +
        `"${bisher.company}" zugeordnet, laut Mandantenzuordnung aber "${kunde.company}". ` +
        'Die Mandantenzuordnung hat Vorrang.'
      );
    }

    map.set(row.datev_mandantennummer, kunde);
  }
}

/**
 * Kundenliste je DATEV-Mandantennummer inklusive Mandantenzuordnungen.
 */
async function ladeKundenJeMandantennummer(beraternummer: string | null): Promise<Map<string, CustomerLookup>> {
  const map = await loadCustomersByMandantennummer();
  await wendeMandantenZuordnungenAn(map, beraternummer);
  return map;
}

/**
 * Führt Mandantenblöcke zusammen, die demselben Kunden zugeordnet sind.
 *
 * Der Fall entsteht nur über eine Mandantenzuordnung (z.B. SEC Selecta auf PROSEON).
 * Ohne Zusammenführung würden zwei Blöcke dieselbe Rechnungsposition schreiben
 * wollen, und der Dublettenschutz würde die zweite stillschweigend verwerfen.
 * Zusammengeführt wird nach demselben Muster wie beim eigenen Bestand im Parser:
 * Posten werden vereint, Kopfsummen addiert, die Teilbeträge bleiben in
 * `zusammensetzung` nachvollziehbar.
 *
 * Arbeitet auf Kopien; das Parse-Ergebnis bleibt unverändert.
 */
function fasseBloeckeJeKundeZusammen(
  mandanten: ParsedMandant[],
  customerMap: Map<string, CustomerLookup>
): ParsedMandant[] {
  const gruppen = new Map<string, ParsedMandant[]>();
  for (const m of mandanten) {
    if (m.istBeraterBlock) continue;
    const kunde = customerMap.get(m.datevMandantennummer);
    if (!kunde) continue;
    const liste = gruppen.get(kunde.id) || [];
    liste.push(m);
    gruppen.set(kunde.id, liste);
  }

  const ergebnis: ParsedMandant[] = [];
  const verarbeitet = new Set<ParsedMandant>();

  for (const m of mandanten) {
    if (verarbeitet.has(m)) continue;

    const kunde = m.istBeraterBlock ? undefined : customerMap.get(m.datevMandantennummer);
    const gruppe = kunde ? (gruppen.get(kunde.id) || [m]) : [m];

    if (gruppe.length <= 1) {
      ergebnis.push(m);
      verarbeitet.add(m);
      continue;
    }

    const primaer = gruppe.find(b => kunde && b.datevMandantennummer === kunde.mandantennummer) || gruppe[0];
    const zusammengefuehrt: ParsedMandant = {
      ...primaer,
      positionen: gruppe.reduce<ParsedMandant['positionen']>((acc, b) => acc.concat(b.positionen), []),
      gesamtbetragLautDatev: round2(gruppe.reduce((sum, b) => sum + b.gesamtbetragLautDatev, 0)),
      zusammensetzung: gruppe.map(b => ({
        bezeichnung: `Mandant ${b.datevMandantennummer} ${b.datevName}`,
        summe: round2(b.gesamtbetragLautDatev)
      })),
      ungeparsteZeilen: gruppe.reduce<string[]>((acc, b) => acc.concat(b.ungeparsteZeilen), [])
    };

    console.log(
      `[DatevWeiterberechnung] Blöcke ${gruppe.map(b => b.datevMandantennummer).join(', ')} ` +
      `über die Mandantenzuordnung zusammengeführt (Kunde "${kunde ? kunde.company : ''}")`
    );

    ergebnis.push(zusammengefuehrt);
    gruppe.forEach(b => verarbeitet.add(b));
  }

  return ergebnis;
}

// ============================================
// RANGLISTEN
// ============================================

/**
 * Baut die Artikel-Rangliste über die gesamte DATEV-Rechnung.
 *
 * Gruppierung nach Artikelnummer (bei Pauschalzeilen ohne Nummer nach Bezeichnung),
 * absteigend nach Nettosumme. Die Mengenspalte ist die Summe der DATEV-Mengen; bei
 * Artikeln, die DATEV in zwei USt-Sätzen ausweist, ist sie entsprechend aufsummiert.
 */
function buildArtikelRanking(parsed: ParsedDatevInvoice): ArtikelRankingEntry[] {
  interface Acc extends ArtikelRankingEntry {
    bloecke: Set<string>;
  }
  const map = new Map<string, Acc>();

  for (const mandant of parsed.mandanten) {
    for (const pos of mandant.positionen) {
      const key = pos.artikelnr || 'P:' + pos.bezeichnung;
      let entry = map.get(key);
      if (!entry) {
        entry = {
          artikelnr: pos.artikelnr,
          bezeichnung: pos.bezeichnung,
          menge: 0,
          gesamtpreis: 0,
          vorkommen: 0,
          aufWhitelist: isWhitelistArtikel(pos.artikelnr),
          bloecke: new Set<string>()
        };
        map.set(key, entry);
      }
      entry.menge += pos.menge;
      entry.gesamtpreis = round2(entry.gesamtpreis + pos.gesamtpreis);
      entry.bloecke.add(mandant.datevMandantennummer);
    }
  }

  const result: ArtikelRankingEntry[] = Array.from(map.values()).map(e => ({
    artikelnr: e.artikelnr,
    bezeichnung: e.bezeichnung,
    menge: e.menge,
    gesamtpreis: e.gesamtpreis,
    vorkommen: e.bloecke.size,
    aufWhitelist: e.aufWhitelist
  }));

  return result.sort((a, b) => b.gesamtpreis - a.gesamtpreis);
}

/**
 * Baut die Kunden-Rangliste über die gesamte DATEV-Rechnung.
 * Sortier- und Anzeigewert ist die DATEV-Kopfsumme des jeweiligen Blocks.
 */
function buildKundenRanking(matches: MandantMatch[]): KundenRankingEntry[] {
  const entries: KundenRankingEntry[] = matches.map(m => ({
    datevMandantennummer: m.datevMandantennummer,
    datevName: m.datevName,
    customerCompany: m.customerCompany,
    status: m.status,
    istBeraterBlock: m.istBeraterBlock,
    datevSumme: m.datevSummeLautKopf,
    weiterberechnet: m.status === 'ok' ? m.totalWeiterberechnung : 0,
    uebersprungenBerechenbar: m.totalSkippedBillable,
    nichtWeiterberechenbar: m.totalNonBillable,
    summenAbweichung: m.summenAbweichung
  }));

  return entries.sort((a, b) => b.datevSumme - a.datevSumme);
}

// ============================================
// VOLLSTÄNDIGKEITSPRÜFUNG
// ============================================

/**
 * Sammelt alle Befunde, die gegen eine Ausführung sprechen.
 *
 * Hintergrund: Erkennt der Parser eine Zeile nicht, verwirft er sie
 * stillschweigend. Das Ergebnis ist keine Fehlermeldung, sondern ein zu
 * niedriger Rechnungsbetrag, der niemandem auffällt. Deshalb wird hier jede
 * Unstimmigkeit zu einem harten Hindernis statt zu einem Hinweis.
 *
 * Geprüft wird auf drei Ebenen:
 * 1. Gesamtrechnung: Summe aller Blockköpfe gegen den Nettobetrag im Rechnungsfuß
 * 2. Einzelner Block: gelesene Posten gegen die Kopfsumme des Blocks
 * 3. Einzelne Zeile: Zeilen, die wie ein Posten aussehen, aber nicht lesbar waren
 */
function sammleVollstaendigkeitsBefunde(analysis: WeiterberechnungAnalysis): string[] {
  const befunde: string[] = [];

  if (analysis.summary.nettoSummeLautPdf === null) {
    befunde.push(
      'Der Nettobetrag konnte nicht aus dem Rechnungsfuß gelesen werden. ' +
      'Die Gegenprobe über die Gesamtrechnung ist damit nicht möglich.'
    );
  } else if (analysis.summary.gesamtAbweichung !== null) {
    befunde.push(
      `Die Summe aller Blöcke (${analysis.summary.totalDatevRechnung.toFixed(2)} EUR) weicht um ` +
      `${analysis.summary.gesamtAbweichung.toFixed(2)} EUR vom Nettobetrag der DATEV-Rechnung ` +
      `(${analysis.summary.nettoSummeLautPdf.toFixed(2)} EUR) ab.`
    );
  }

  for (const m of analysis.matches) {
    if (m.summenAbweichung !== null) {
      befunde.push(
        `${m.datevMandantennummer} ${m.datevName}: gelesene Posten ${m.parsedSumme.toFixed(2)} EUR, ` +
        `laut DATEV ${m.datevSummeLautKopf.toFixed(2)} EUR, Differenz ${m.summenAbweichung.toFixed(2)} EUR.`
      );
    }
    for (const zeile of m.ungeparsteZeilen) {
      befunde.push(`${m.datevMandantennummer} ${m.datevName}: Zeile nicht lesbar: "${zeile}"`);
    }
  }

  return befunde;
}

// ============================================
// PUBLIC API
// ============================================

export const datevWeiterberechnungService = {
  /**
   * Kundenliste je DATEV-Mandantennummer inklusive Mandantenzuordnungen.
   * Gemeinsame Grundlage für Weiterberechnung und Kostenerfassung.
   */
  async ladeMandantenAufloesung(beraternummer: string | null): Promise<Map<string, CustomerLookup>> {
    return ladeKundenJeMandantennummer(beraternummer);
  },

  /**
   * Analysiert die geparste DATEV-Rechnung: matcht Mandanten gegen customers-Tabelle,
   * filtert Positionen auf Whitelist, aggregiert und ermittelt die Zielrechnung.
   *
   * Ändert NICHTS in der Datenbank.
   */
  async analyze(parsed: ParsedDatevInvoice): Promise<WeiterberechnungAnalysis> {
    const customerMap = await ladeKundenJeMandantennummer(parsed.beraternummer);
    const bloecke = fasseBloeckeJeKundeZusammen(parsed.mandanten, customerMap);
    const matches: MandantMatch[] = [];

    for (const mandant of bloecke) {
      if (mandant.istBeraterBlock) {
        matches.push(buildBeraterBlockMatch(mandant));
        continue;
      }
      const match = await analyzeSingleMandant(
        mandant,
        customerMap,
        parsed.year,
        parsed.month
      );
      matches.push(match);
    }

    // Summary berechnen
    const summary = {
      totalMatches: matches.length,
      matchesOk: matches.filter(m => m.status === 'ok').length,
      matchesNoWhitelist: matches.filter(m => m.status === 'no_whitelist_items').length,
      matchesSammelkunde: matches.filter(m => m.status === 'sammelkunde_skipped').length,
      matchesNotFound: matches.filter(m => m.status === 'customer_not_found').length,
      matchesDeleted: matches.filter(m => m.status === 'customer_deleted').length,
      totalBetragNetto: matches
        .filter(m => m.status === 'ok')
        .reduce((sum, m) => sum + m.totalWeiterberechnung, 0),
      matchesBeraterBlock: matches.filter(m => m.status === 'berater_block').length,
      totalDatevRechnung: round2(matches.reduce((sum, m) => sum + m.datevSummeLautKopf, 0)),
      totalNichtWeiterberechenbar: round2(matches.reduce((sum, m) => sum + m.totalNonBillable, 0)),
      totalUebersprungenBerechenbar: round2(matches.reduce((sum, m) => sum + m.totalSkippedBillable, 0)),
      bloeckeMitAbweichung: matches.filter(m => m.summenAbweichung !== null).length,
      bloeckeMitUngeparstenZeilen: matches.filter(m => m.ungeparsteZeilen.length > 0).length,
      totalUngeparsteZeilen: matches.reduce((sum, m) => sum + m.ungeparsteZeilen.length, 0),
      nettoSummeLautPdf: parsed.nettoSummeLautPdf,
      gesamtAbweichung: null as number | null
    };

    // Gesamtgegenprobe: Die Summe aller Blockkopfsummen muss dem Nettobetrag
    // aus dem Rechnungsfuß entsprechen. Fehlt ein Block vollständig, fällt das
    // hier auf, während die blockinterne Prüfung nichts merken würde.
    if (parsed.nettoSummeLautPdf !== null) {
      const diff = round2(summary.totalDatevRechnung - parsed.nettoSummeLautPdf);
      summary.gesamtAbweichung = Math.abs(diff) > GESAMTSUMMEN_TOLERANZ ? diff : null;
    }

    return {
      sourceYear: parsed.year,
      sourceMonth: parsed.month,
      matches,
      summary,
      artikelRanking: buildArtikelRanking(parsed),
      kundenRanking: buildKundenRanking(matches)
    };
  },

  /**
   * Prüft, ob das Analyse-Ergebnis vollständig genug für eine Ausführung ist.
   * Leeres Ergebnis bedeutet: Ausführung unbedenklich.
   */
  pruefeVollstaendigkeit(analysis: WeiterberechnungAnalysis): string[] {
    return sammleVollstaendigkeitsBefunde(analysis);
  },

  /**
   * Führt die Weiterberechnung aus: fügt Positionen in bestehende Entwürfe ein
   * bzw. legt neue Entwürfe an.
   *
   * Verarbeitet nur Matches mit status='ok'.
   */
  async execute(
    analysis: WeiterberechnungAnalysis,
    options: ExecuteOptions = {}
  ): Promise<ExecutionResult> {
    // Harter Stopp: Solange die Rechnung nicht nachweislich vollständig gelesen
    // wurde, wird nichts geschrieben. Eine unvollständig gelesene Rechnung führt
    // sonst zu stillschweigend zu niedrigen Kundenrechnungen.
    const befunde = sammleVollstaendigkeitsBefunde(analysis);
    if (befunde.length > 0 && !options.trotzBefundenAusfuehren) {
      throw new Error(
        'Die DATEV-Rechnung wurde nicht vollständig gelesen. Es wurde nichts geschrieben.\n\n' +
        befunde.join('\n')
      );
    }
    if (befunde.length > 0 && options.trotzBefundenAusfuehren) {
      console.warn(
        '[DatevWeiterberechnung] Ausführung trotz offener Befunde ausdrücklich freigegeben:\n' +
        befunde.join('\n')
      );
    }

    const result: ExecutionResult = {
      success: true,
      createdInvoices: [],
      appendedInvoices: [],
      skippedPositions: [],
      errors: []
    };

    const okMatches = analysis.matches.filter(m => m.status === 'ok');

    for (const match of okMatches) {
      if (!match.customerId || !match.targetInvoice) continue;

      try {
        const { targetInvoice, aggregatedPositions, customerId, customerCompany } = match;

        // Rechnung ermitteln (entweder existierend oder neu anlegen)
        let invoiceId: string;

        if (targetInvoice.existingInvoiceId) {
          // Bestehenden Entwurf verwenden
          invoiceId = targetInvoice.existingInvoiceId;
        } else {
          // Neuen Entwurf anlegen
          invoiceId = await createNewDraftInvoice(
            customerId,
            targetInvoice.targetYear,
            targetInvoice.targetMonth
          );
          result.createdInvoices.push({
            customerId,
            customerCompany: customerCompany || '',
            invoiceId,
            year: targetInvoice.targetYear,
            month: targetInvoice.targetMonth
          });
        }

        // Positionen einfügen (bereits vorhandene werden übersprungen)
        const einfuegeErgebnis = await appendPositionsToInvoice(
          invoiceId,
          aggregatedPositions,
          analysis.sourceYear,
          analysis.sourceMonth
        );

        // Rechnungsbeträge neu berechnen
        await invoiceService.recalculateInvoiceTotals(invoiceId);

        for (const beschreibung of einfuegeErgebnis.skipped) {
          result.skippedPositions.push({
            customerId,
            customerCompany: customerCompany || '',
            invoiceId,
            beschreibung
          });
        }

        // Wenn wir einen bestehenden Entwurf angereichert haben (nicht neu angelegt)
        if (targetInvoice.existingInvoiceId) {
          result.appendedInvoices.push({
            customerId,
            customerCompany: customerCompany || '',
            invoiceId,
            year: targetInvoice.targetYear,
            month: targetInvoice.targetMonth,
            positionsAdded: einfuegeErgebnis.inserted
          });
        }
      } catch (err) {
        result.errors.push({
          customerId: match.customerId,
          customerCompany: match.customerCompany || '',
          error: err instanceof Error ? err.message : 'Unbekannter Fehler'
        });
      }
    }

    if (result.errors.length > 0) {
      result.success = false;
    }

    return result;
  }
};

// ============================================
// INTERNAL: Beraterbezogenen Block abbilden
// ============================================

/**
 * Bildet den Block "Beraterbezogene Leistungen" als Match-Eintrag ab.
 *
 * Dieser Block ist keinem Mandanten zugeordnet und enthält die Kosten der Kanzlei
 * selbst. Er erhält den Status 'berater_block', der niemals 'ok' wird – da execute()
 * ausschliesslich 'ok'-Matches verarbeitet, ist eine Weiterberechnung konstruktiv
 * ausgeschlossen. Alle Posten werden als nicht weiterberechenbar geführt.
 */
function buildBeraterBlockMatch(mandant: ParsedMandant): MandantMatch {
  const analyse = buildAnalyseFelder(mandant, true);

  return {
    datevMandantennummer: mandant.datevMandantennummer,
    datevName: mandant.datevName,
    status: 'berater_block' as MatchStatus,
    customerId: null,
    customerCompany: null,
    aggregatedPositions: [],
    totalWeiterberechnung: 0,
    targetInvoice: null,
    warning: 'Kosten der Kanzlei ohne Mandantenbezug. Wird nicht weiterberechnet.',
    istBeraterBlock: true,
    skippedBillablePositions: [],
    totalSkippedBillable: 0,
    nonBillablePositions: analyse.nonBillablePositions,
    totalNonBillable: analyse.totalNonBillable,
    datevSummeLautKopf: analyse.datevSummeLautKopf,
    parsedSumme: analyse.parsedSumme,
    summenAbweichung: analyse.summenAbweichung,
    ungeparsteZeilen: analyse.ungeparsteZeilen,
    zusammensetzung: mandant.zusammensetzung
  };
}

// ============================================
// INTERNAL: Einzelnen Mandant analysieren
// ============================================

async function analyzeSingleMandant(
  mandant: ParsedMandant,
  customerMap: Map<string, CustomerLookup>,
  sourceYear: number,
  sourceMonth: number
): Promise<MandantMatch> {
  const analyse = buildAnalyseFelder(mandant, false);

  const base = {
    datevMandantennummer: mandant.datevMandantennummer,
    datevName: mandant.datevName,
    aggregatedPositions: [] as AggregatedPosition[],
    totalWeiterberechnung: 0,
    targetInvoice: null as TargetInvoiceInfo | null,
    warning: null as string | null,
    istBeraterBlock: false,
    skippedBillablePositions: [] as AggregatedPosition[],
    totalSkippedBillable: 0,
    nonBillablePositions: analyse.nonBillablePositions,
    totalNonBillable: analyse.totalNonBillable,
    datevSummeLautKopf: analyse.datevSummeLautKopf,
    parsedSumme: analyse.parsedSumme,
    summenAbweichung: analyse.summenAbweichung,
    ungeparsteZeilen: analyse.ungeparsteZeilen,
    zusammensetzung: mandant.zusammensetzung
  };

  const customer = customerMap.get(mandant.datevMandantennummer);

  // Whitelist-Posten des Blocks (bei übersprungenen Kunden nur zur Information)
  const whitelistPositions = aggregateWhitelistPositions(mandant);
  const whitelistSumme = round2(
    whitelistPositions.reduce((sum, p) => sum + p.gesamtpreis, 0)
  );

  // Kein Kunde gefunden
  if (!customer) {
    return {
      ...base,
      status: 'customer_not_found' as MatchStatus,
      customerId: null,
      customerCompany: null,
      skippedBillablePositions: whitelistPositions,
      totalSkippedBillable: whitelistSumme,
      warning: `Mandantennummer ${mandant.datevMandantennummer} existiert nicht in der Kundenstammdaten.`
    };
  }

  // Kunde gelöscht
  if (customer.deleted) {
    return {
      ...base,
      status: 'customer_deleted' as MatchStatus,
      customerId: customer.id,
      customerCompany: customer.company,
      skippedBillablePositions: whitelistPositions,
      totalSkippedBillable: whitelistSumme,
      warning: `Kunde "${customer.company}" ist in den Stammdaten als gelöscht markiert.`
    };
  }

  // Sammelkunde überspringen
  if (customer.auftraggeber_id) {
    return {
      ...base,
      status: 'sammelkunde_skipped' as MatchStatus,
      customerId: customer.id,
      customerCompany: customer.company,
      skippedBillablePositions: whitelistPositions,
      totalSkippedBillable: whitelistSumme,
      warning: `Kunde "${customer.company}" ist einer Sammelrechnung zugeordnet und wird übersprungen.`
    };
  }

  // Whitelist-Positionen aggregieren
  const aggregatedPositions = whitelistPositions;

  if (aggregatedPositions.length === 0) {
    return {
      ...base,
      status: 'no_whitelist_items' as MatchStatus,
      customerId: customer.id,
      customerCompany: customer.company
    };
  }

  const totalWeiterberechnung = Math.round(
    aggregatedPositions.reduce((sum, p) => sum + p.gesamtpreis, 0) * 100
  ) / 100;

  // Zielrechnung ermitteln
  const targetInvoice = await determineTargetInvoice(customer.id, sourceYear, sourceMonth);

  return {
    ...base,
    status: 'ok' as MatchStatus,
    customerId: customer.id,
    customerCompany: customer.company,
    aggregatedPositions,
    totalWeiterberechnung,
    targetInvoice
  };
}

// ============================================
// INTERNAL: Neuen Rechnungsentwurf anlegen
// ============================================

/**
 * Legt einen neuen Rechnungsentwurf für einen Einzelkunden (kein Sammelkunde) an.
 *
 * Logik analog zu invoiceService.createDraft, aber ohne Lohnabrechnungs-Position
 * (die DATEV-Posten werden anschließend separat eingefügt).
 */
async function createNewDraftInvoice(
  customerId: string,
  year: number,
  month: number
): Promise<string> {
  // Kundendaten laden
  const { data: customer, error: customerError } = await supabase
    .from('customers')
    .select(`
      *,
      zustaendiger_mitarbeiter:xtax_staff!customers_zustaendiger_mitarbeiter_fkey (
        full_name
      )
    `)
    .eq('id', customerId)
    .single();

  if (customerError || !customer) {
    throw new Error('Kunde konnte nicht geladen werden: ' + (customerError?.message || ''));
  }

  // Einstellungen für USt-Satz
  const settings = await invoiceService.getSettings();
  const ustSatz = customer.reverse_charge ? 0 : (settings?.ust_satz ?? 19);

  // Leistungszeitraum berechnen
  const letzterTag = new Date(year, month, 0).getDate();
  const leistungszeitraumVon = `${year}-${String(month).padStart(2, '0')}-01`;
  const leistungszeitraumBis = `${year}-${String(month).padStart(2, '0')}-${String(letzterTag).padStart(2, '0')}`;

  // Empfänger-Person: ein abweichender Rechnungsempfänger am Kunden hat Vorrang
  // vor dem Ansprechpartner. Gemeinsame Regel mit dem invoiceService.
  const empfaengerPerson = buildRechnungsEmpfaengerPerson(customer);

  const ansprechpartner = customer.zustaendiger_mitarbeiter?.full_name || null;

  const { data: invoice, error: insertError } = await supabase
    .from('invoices')
    .insert({
      customer_id: customerId,
      year,
      month,
      status: 'entwurf',
      leistungszeitraum_von: leistungszeitraumVon,
      leistungszeitraum_bis: leistungszeitraumBis,
      ansprechpartner_name: ansprechpartner,
      netto_betrag: 0,
      ust_satz: ustSatz,
      ust_betrag: 0,
      brutto_betrag: 0,
      empfaenger_firma: customer.company,
      empfaenger_anrede: empfaengerPerson.anrede,
      empfaenger_vorname: empfaengerPerson.vorname,
      empfaenger_nachname: empfaengerPerson.nachname,
      empfaenger_strasse: customer.strasse || null,
      empfaenger_plz: customer.plz || null,
      empfaenger_ort: customer.ort || null,
      empfaenger_land: customer.land || 'Deutschland',
      empfaenger_kundennummer: customer.kundennummer_rechnung || null
    })
    .select()
    .single();

  if (insertError || !invoice) {
    throw new Error('Entwurf konnte nicht erstellt werden: ' + (insertError?.message || ''));
  }

  return invoice.id;
}

// ============================================
// INTERNAL: Positionen an Rechnung anhängen
// ============================================

/**
 * Hängt die DATEV-Positionen an die angegebene Rechnung an.
 * Bestimmt die nächste freie Positionsnummer und erstellt die Beschreibung
 * im Format: "{artikelnr} {bezeichnung} (DATEV MM/YYYY)".
 *
 * Dublettenschutz: Existiert auf der Zielrechnung bereits eine Position mit
 * genau dieser Beschreibung, wird sie nicht erneut eingefügt. Damit bleibt ein
 * Wiederholungslauf derselben DATEV-Rechnung folgenlos, etwa nach einem
 * Abbruch mittendrin oder nach einer Parser-Korrektur. Die Beschreibung ist
 * eindeutig aus Artikelnummer und DATEV-Monat aufgebaut und taugt deshalb als
 * Erkennungsmerkmal.
 *
 * Achtung: Liefert DATEV eine korrigierte Rechnung mit geänderten Mengen für
 * denselben Monat, greift der Schutz ebenfalls. Die geänderte Position muss
 * dann bewusst von Hand angepasst werden.
 *
 * @returns Anzahl eingefügter Positionen und Beschreibungen der übersprungenen
 */
async function appendPositionsToInvoice(
  invoiceId: string,
  positions: AggregatedPosition[],
  sourceYear: number,
  sourceMonth: number
): Promise<{ inserted: number; skipped: string[] }> {
  if (positions.length === 0) return { inserted: 0, skipped: [] };

  // Bestehende Positionen laden: höchste Positionsnummer und Beschreibungen
  const { data: existingItems, error: loadError } = await supabase
    .from('invoice_line_items')
    .select('position, beschreibung')
    .eq('invoice_id', invoiceId)
    .order('position', { ascending: false });

  if (loadError) {
    throw new Error('Bestehende Positionen konnten nicht geladen werden: ' + loadError.message);
  }

  const vorhandeneBeschreibungen = new Set(
    (existingItems || []).map(item => (item.beschreibung || '').trim())
  );

  let nextPosition = existingItems && existingItems.length > 0
    ? existingItems[0].position + 1
    : 1;

  const monthPadded = String(sourceMonth).padStart(2, '0');
  const datevSuffix = `(DATEV ${monthPadded}/${sourceYear})`;

  const skipped: string[] = [];
  const inserts: Array<{
    invoice_id: string;
    position: number;
    beschreibung: string;
    menge: number;
    einheit: string;
    einzelpreis: number;
    gesamtpreis: number;
  }> = [];

  for (const pos of positions) {
    const beschreibung = `${pos.artikelnr} ${pos.bezeichnung} ${datevSuffix}`;

    if (vorhandeneBeschreibungen.has(beschreibung)) {
      skipped.push(beschreibung);
      console.warn(
        `[DatevWeiterberechnung] Position bereits vorhanden, übersprungen: ` +
        `"${beschreibung}" (Rechnung ${invoiceId})`
      );
      continue;
    }

    inserts.push({
      invoice_id: invoiceId,
      position: nextPosition++,
      beschreibung,
      menge: pos.menge,
      einheit: pos.einheit,
      einzelpreis: pos.einzelpreis,
      gesamtpreis: pos.gesamtpreis
    });
  }

  if (inserts.length === 0) {
    return { inserted: 0, skipped };
  }

  const { error } = await supabase
    .from('invoice_line_items')
    .insert(inserts);

  if (error) {
    throw new Error('Positionen konnten nicht eingefügt werden: ' + error.message);
  }

  return { inserted: inserts.length, skipped };
}
